import { AlertTriangle, Check, CircleDot, Clock } from 'lucide-react'
import type { ReactNode } from 'react'

import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import type { Bar, BarState } from '../domain'

/**
 * Estado → color, ícono y texto. LOS TRES, siempre.
 *
 * La regla de la consola: ningún estado se comunica sólo con color. Acá pesa
 * más que en una tabla, porque una barra es una mancha: sin el ícono adentro y
 * sin el texto en el `title`, "vencido" y "en curso" son dos rectángulos que se
 * distinguen únicamente por su tono.
 */
const ESTADO: Record<BarState, { token: string; icon: ReactNode; labelKey: string }> = {
  overdue: {
    token: '--overdue',
    icon: <AlertTriangle size={10} aria-hidden="true" />,
    labelKey: 'timeline.state.overdue',
  },
  active: {
    token: '--phase-in-development',
    icon: <CircleDot size={10} aria-hidden="true" />,
    labelKey: 'timeline.state.active',
  },
  done: {
    token: '--phase-done',
    icon: <Check size={10} aria-hidden="true" />,
    labelKey: 'timeline.state.done',
  },
  upcoming: {
    token: '--phase-not-started',
    icon: <Clock size={10} aria-hidden="true" />,
    labelKey: 'timeline.state.upcoming',
  },
}

export interface TimelineBarProps {
  bar: Bar
  /** Lo que se lee al pasar el mouse y lo que anuncia el lector de pantalla. */
  label: string
}

export function TimelineBar({ bar, label }: TimelineBarProps) {
  const t = useT()
  const estado = ESTADO[bar.state]

  return (
    <span
      title={`${label} · ${t(estado.labelKey)}`}
      style={{
        left: `${String(bar.leftPct)}%`,
        width: `${String(bar.widthPct)}%`,
        backgroundColor: `hsl(var(${estado.token}) / 0.18)`,
        borderColor: `hsl(var(${estado.token}))`,
        color: `hsl(var(${estado.token}))`,
      }}
      className={cn(
        'absolute top-1/2 flex h-4 -translate-y-1/2 items-center gap-1 overflow-hidden border px-1',
        // Los bordes rectos dicen "esto sigue afuera de la ventana"; los
        // redondeados, "acá empieza / acá termina de verdad".
        bar.clippedStart ? 'rounded-l-none border-l-0' : 'rounded-l-[var(--radius-control)]',
        bar.clippedEnd ? 'rounded-r-none border-r-0' : 'rounded-r-[var(--radius-control)]',
      )}
    >
      {estado.icon}
    </span>
  )
}
